import type { Agent, Coordinator, Team } from '@/types'

import {
    Activity,
    BrainCircuit,
    Bug,
    CalendarCheck,
    Contact,
    DatabaseZap,
    FileCode,
    FlaskConical,
    Gauge,
    Inbox,
    Magnet,
    Mail,
    MailPlus,
    Megaphone,
    Newspaper,
    PenLine,
    Reply,
    Search,
    Share2,
    ShieldCheck,
    Siren,
    Target,
    Telescope,
    UserSearch,
    Wrench
} from 'lucide-react'

export const COORDINATOR_COLOR = '#a78bfa'

export const coordinator: Coordinator = {
    name: 'Coordinator',
    role: "The only agent you talk to. It turns your request into a plan, hands each task to the right specialist, checks the work and reports back in one place.",
    example: "Split one launch request into 14 tasks across all four teams.",
    icon: BrainCircuit
}

export const teams: Team[] = [
    {
        id: 'marketing',
        name: 'Marketing',
        tagline: 'Promotes your product',
        summary: "Writes the content, runs the channels and keeps your product in front of the right audience.",
        color: '#e879f9',
        icon: Megaphone,
        agents: [
            {
                id: 'content-writer',
                name: 'Content Writer',
                role: "Writes blog posts, landing page copy and launch announcements in your brand voice.",
                example: "Drafted a 1,200 word launch post for your new feature.",
                icon: PenLine
            },
            {
                id: 'social-manager',
                name: 'Social Media Manager',
                role: "Plans, writes and schedules posts across your social channels.",
                example: "Scheduled 6 launch posts for LinkedIn and X.",
                icon: Share2
            },
            {
                id: 'seo-strategist',
                name: 'SEO Strategist',
                role: "Finds the searches your buyers make and shapes pages so they rank for them.",
                example: "Picked 12 keywords for your pricing page.",
                icon: Search
            },
            {
                id: 'ads-manager',
                name: 'Ads Manager',
                role: "Builds paid campaigns, tests new angles and moves budget to what works.",
                example: "Launched 3 ad variations and paused the weakest.",
                icon: Target
            },
            {
                id: 'market-researcher',
                name: 'Market Researcher',
                role: "Tracks competitors, pricing moves and trends so your messaging stays sharp.",
                example: "Summarised what two competitors shipped this month.",
                icon: Telescope
            }
        ]
    },
    {
        id: 'leads',
        name: 'Lead Generation',
        tagline: 'Fills your pipeline',
        summary: "Finds the companies most likely to buy, learns about them and gets them ready for outreach.",
        color: '#22d3ee',
        icon: Magnet,
        agents: [
            {
                id: 'prospect-finder',
                name: 'Prospect Finder',
                role: "Searches for companies and people who match your best customers.",
                example: "Found 200 companies that look like your top accounts.",
                icon: UserSearch
            },
            {
                id: 'lead-enricher',
                name: 'Lead Enricher',
                role: "Adds the missing details: role, company size, industry and the tools they use.",
                example: "Completed contact details for 186 new leads.",
                icon: DatabaseZap
            },
            {
                id: 'lead-scorer',
                name: 'Lead Scorer',
                role: "Ranks every lead by how ready they are to buy, so the best ones get attention first.",
                example: "Flagged 24 leads as ready for a sales call.",
                icon: Gauge
            },
            {
                id: 'crm-keeper',
                name: 'CRM Keeper',
                role: "Keeps your customer list clean, removes duplicates and logs every touchpoint.",
                example: "Merged 41 duplicate contacts and updated deal stages.",
                icon: Contact
            },
            {
                id: 'meeting-booker',
                name: 'Meeting Booker',
                role: "Turns interested replies into meetings on your calendar.",
                example: "Booked 5 demos for next week.",
                icon: CalendarCheck
            }
        ]
    },
    {
        id: 'email',
        name: 'Email Outreach',
        tagline: 'Writes and sends your mail',
        summary: "Writes personal emails, follows up at the right time and keeps your messages out of spam.",
        color: '#fbbf24',
        icon: Mail,
        agents: [
            {
                id: 'cold-email-writer',
                name: 'Cold Email Writer',
                role: "Writes a personal first email for every prospect, based on what matters to them.",
                example: "Wrote 200 first emails, each one personal.",
                icon: MailPlus
            },
            {
                id: 'follow-up-writer',
                name: 'Follow-up Writer',
                role: "Builds follow-up sequences that stop as soon as someone replies.",
                example: "Set up a 3 step follow-up for new prospects.",
                icon: Reply
            },
            {
                id: 'newsletter-writer',
                name: 'Newsletter Writer',
                role: "Turns your updates and wins into a newsletter your customers actually open.",
                example: "Drafted this month's product update newsletter.",
                icon: Newspaper
            },
            {
                id: 'reply-handler',
                name: 'Reply Handler',
                role: "Sorts incoming replies, answers common questions and passes hot ones to you.",
                example: "Answered 18 questions and passed 3 hot replies to you.",
                icon: Inbox
            },
            {
                id: 'deliverability-guard',
                name: 'Deliverability Guard',
                role: "Watches bounce and spam rates and adjusts sending so your emails land in the inbox.",
                example: "Slowed sending on one domain after a bounce spike.",
                icon: ShieldCheck
            }
        ]
    },
    {
        id: 'bugs',
        name: 'Bug Fixing',
        tagline: 'Keeps your product healthy',
        summary: "Finds what is broken, fixes it, checks the fix and watches every release.",
        color: '#34d399',
        icon: Bug,
        agents: [
            {
                id: 'bug-triager',
                name: 'Bug Triager',
                role: "Reads every bug report, reproduces the problem and ranks it by impact.",
                example: "Reproduced the checkout bug in 4 minutes.",
                icon: Siren
            },
            {
                id: 'bug-fixer',
                name: 'Bug Fixer',
                role: "Finds the root cause and writes the fix.",
                example: "Fixed the checkout bug and two related issues.",
                icon: Wrench
            },
            {
                id: 'code-reviewer',
                name: 'Code Reviewer',
                role: "Reviews every fix for side effects before it moves on.",
                example: "Approved the fix after one round of changes.",
                icon: FileCode
            },
            {
                id: 'qa-tester',
                name: 'QA Tester',
                role: "Tests the product end to end so a fix never breaks something else.",
                example: "Ran 140 checks on checkout and sign up.",
                icon: FlaskConical
            },
            {
                id: 'release-monitor',
                name: 'Release Monitor',
                role: "Watches errors and speed after every release and raises the alarm early.",
                example: "Confirmed zero new errors 24 hours after release.",
                icon: Activity
            }
        ]
    }
]

export const agentCount = 1 + teams.reduce((total, team) => total + team.agents.length, 0)

export const agentLookup = new Map<string, { agent: Agent, team: Team }>(
    teams.flatMap((team) => team.agents.map((agent): [string, { agent: Agent, team: Team }] => [agent.id, { agent, team }]))
)
